// ByteBot Unified Design System
export const theme = {
  // Color Palette
  colors: {
    primary: '#007acc',
    primaryDark: '#005a99',
    primaryLight: '#1a8cdd',
    secondary: '#333',
    background: '#f8f9fa',
    surface: '#ffffff',
    text: '#333',
    textLight: '#666',
    textMuted: '#999',
    border: '#ddd',
    borderDark: '#333',
    success: '#28a745',
    error: '#dc3545',
    warning: '#ffc107',
    accent: '#007acc',
  },

  // Typography
  typography: {
    fontFamily: "'Inter', 'Arial', sans-serif",
    fontSizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
      '5xl': '3rem',
      '6xl': '3.75rem',
    },
    fontWeights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },

  // Spacing
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
    '4xl': '6rem',
  },

  // Border Radius
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  // Breakpoints
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
  },
};

// Component Styles
export const components = {
  // Button variants
  button: {
    base: {
      padding: '0.75rem 1.5rem',
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.semibold,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      border: 'none',
      outline: 'none',
    },
    primary: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.colors.primaryDark,
        transform: 'translateY(-1px)',
        boxShadow: theme.shadows.md,
      },
    },
    secondary: {
      backgroundColor: 'transparent',
      color: theme.colors.secondary,
      border: `2px solid ${theme.colors.secondary}`,
      '&:hover': {
        backgroundColor: theme.colors.secondary,
        color: '#fff',
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.primary,
      '&:hover': {
        backgroundColor: `${theme.colors.primary}10`,
      },
    },
  },

  // Card styles
  card: {
    base: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xl,
      boxShadow: theme.shadows.md,
      border: `1px solid ${theme.colors.border}`,
    },
    elevated: {
      boxShadow: theme.shadows.lg,
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows.xl,
      },
    },
  },

  // Input styles
  input: {
    base: {
      padding: '0.75rem',
      border: `2px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      fontSize: theme.typography.fontSizes.base,
      fontFamily: theme.typography.fontFamily,
      width: '100%',
      '&:focus': {
        borderColor: theme.colors.primary,
        outline: 'none',
        boxShadow: `0 0 0 3px ${theme.colors.primary}20`,
      },
    },
  },

  // Navigation styles
  nav: {
    base: {
      backgroundColor: theme.colors.surface,
      borderBottom: `2px solid ${theme.colors.borderDark}`,
      padding: `0 ${theme.spacing.lg}`,
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      boxShadow: theme.shadows.md,
    },
  },
};

// Utility functions
export const createHoverEffect = (baseColor, hoverColor) => ({
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: hoverColor,
    transform: 'translateY(-1px)',
  },
});

export const createGradient = (color1, color2, direction = 'to right') => ({
  background: `linear-gradient(${direction}, ${color1}, ${color2})`,
});

export const responsiveContainer = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: `0 ${theme.spacing.lg}`,
}; 